import React from 'react';
import { MessageCircle, Percent, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export const BulkDiscountSettings = ({ settings, updateSetting }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Percent className="h-5 w-5 text-green-600" />
        Bulk Discounts
      </CardTitle>
      <CardDescription>Configure volume-based pricing</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="bulk-enabled">Enable Bulk Discounts</Label>
        <Switch id="bulk-enabled" checked={settings?.bulk_discount_enabled} onCheckedChange={(v) => updateSetting('bulk_discount_enabled', v)} />
      </div>
      <div className="space-y-2">
        <Label>Minimum orders for bulk discount</Label>
        <Input type="number" value={settings?.bulk_discount_threshold || 5} onChange={(e) => updateSetting('bulk_discount_threshold', parseInt(e.target.value))} />
      </div>
      <div className="space-y-2">
        <Label>Bulk discount (%)</Label>
        <Input type="number" value={settings?.bulk_discount_percent || 10} onChange={(e) => updateSetting('bulk_discount_percent', parseFloat(e.target.value))} />
      </div>
    </CardContent>
  </Card>
);

export const CommunicationSettings = ({ settings, updateSetting }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-cyan-600" />
        Communication Settings
      </CardTitle>
      <CardDescription>Configure auto-responses and notifications</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex items-center justify-between">
        <Label htmlFor="auto-wa">Auto WhatsApp Responses</Label>
        <Switch id="auto-wa" checked={settings?.auto_whatsapp_enabled} onCheckedChange={(v) => updateSetting('auto_whatsapp_enabled', v)} />
      </div>
      <div className="flex items-center justify-between">
        <Label htmlFor="auto-email">Auto Email Responses</Label>
        <Switch id="auto-email" checked={settings?.auto_email_enabled} onCheckedChange={(v) => updateSetting('auto_email_enabled', v)} />
      </div>
      <div className="space-y-2">
        <Label>WhatsApp response delay (minutes)</Label>
        <Input type="number" value={settings?.whatsapp_response_interval_minutes || 5} onChange={(e) => updateSetting('whatsapp_response_interval_minutes', parseInt(e.target.value))} />
      </div>
      <div className="space-y-2 border-t pt-4">
        <Label>Webhook API Key</Label>
        <Input type="text" value={settings?.webhook_api_key || ''} onChange={(e) => updateSetting('webhook_api_key', e.target.value)} placeholder="Leave empty to allow all webhook requests" />
        <p className="text-xs text-gray-500">Used to authenticate external webhook requests</p>
      </div>
    </CardContent>
  </Card>
);

export const BusinessHoursSettings = ({ settings, updateSetting }) => (
  <Card className="md:col-span-2">
    <CardHeader>
      <CardTitle className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-blue-600" />
        Business Hours
      </CardTitle>
      <CardDescription>Set operating hours for automated responses</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-4">
          <h4 className="font-medium">Weekdays (Mon-Fri)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start</Label>
              <Input type="time" value={settings?.business_hours_start || '07:00'} onChange={(e) => updateSetting('business_hours_start', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End</Label>
              <Input type="time" value={settings?.business_hours_end || '19:00'} onChange={(e) => updateSetting('business_hours_end', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <h4 className="font-medium">Weekends (Sat)</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start</Label>
              <Input type="time" value={settings?.weekend_hours_start || '08:00'} onChange={(e) => updateSetting('weekend_hours_start', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>End</Label>
              <Input type="time" value={settings?.weekend_hours_end || '16:00'} onChange={(e) => updateSetting('weekend_hours_end', e.target.value)} />
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);
